import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Snackbar, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ConfirmationDialog from '../../common/ui/ConfimationDialog';
import { useNavigationGuard } from '../../emailTemplates/templateEditor/components/useNavigationGuard';

export type ChangeEntry = { label: string; from: ReactNode; to: ReactNode };

export type SectionSaveHandle = {
  tabKey: string;
  submit: () => Promise<void>;
  reset: () => void;
  dirty: boolean;
  isSubmitting: boolean;
  isValid: boolean;
  changes: ChangeEntry[];
};

type SnackbarState = { severity: 'success' | 'error'; message: string } | null;

export type SaveHeaderContextValue = {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  requestTabChange: (nextTab: string) => Promise<boolean>;
  registerActive: (handle: SectionSaveHandle | null) => void;
  activeHandle: SectionSaveHandle | null;
  showSuccess: (message?: string) => void;
  showError: (message: string) => void;
};

const SaveHeaderContext = createContext<SaveHeaderContextValue | null>(null);

export const useSaveHeader = (): SaveHeaderContextValue => {
  const ctx = useContext(SaveHeaderContext);
  if (!ctx) {
    throw new Error('useSaveHeader must be used inside SaveHeaderProvider');
  }
  return ctx;
};

type ProviderProps = {
  tabs: string[];
  children: ReactNode;
};

export const SaveHeaderProvider = ({ tabs, children }: ProviderProps) => {
  const { t } = useTranslation();

  const initialTab = useMemo(() => {
    const hash = typeof window !== 'undefined' ? window.location.hash.replace('#', '') : '';
    return hash && tabs.includes(hash) ? hash : tabs[0];
  }, [tabs]);

  const [activeTab, setActiveTab] = useState<string>(initialTab);
  const [activeHandle, setActiveHandleState] = useState<SectionSaveHandle | null>(null);
  const [snackbar, setSnackbar] = useState<SnackbarState>(null);

  const [pendingTabChange, setPendingTabChange] = useState<string | null>(null);
  const pendingResolverRef = useRef<((confirmed: boolean) => void) | null>(null);

  const registerActive = useCallback(
    (handle: SectionSaveHandle | null) => {
      setActiveHandleState((prev) => {
        if (!handle) return prev && prev.tabKey === activeTab ? null : prev;
        return handle;
      });
    },
    [activeTab],
  );

  useEffect(() => {
    setActiveHandleState((prev) => (prev && prev.tabKey === activeTab ? prev : null));
  }, [activeTab]);

  const showSuccess = useCallback(
    (message?: string) => {
      setSnackbar({ severity: 'success', message: message || t('editOrderPage.orderPageTypeSuccess') });
    },
    [t],
  );
  const showError = useCallback((message: string) => {
    setSnackbar({ severity: 'error', message });
  }, []);

  const requestTabChange = useCallback(
    (nextTab: string): Promise<boolean> => {
      if (!activeHandle?.dirty || activeHandle.isSubmitting) {
        return Promise.resolve(true);
      }
      return new Promise<boolean>((resolve) => {
        pendingResolverRef.current = resolve;
        setPendingTabChange(nextTab);
      });
    },
    [activeHandle],
  );

  const closePendingTabChange = (confirmed: boolean) => {
    const resolver = pendingResolverRef.current;
    pendingResolverRef.current = null;
    setPendingTabChange(null);
    resolver?.(confirmed);
  };

  const { NavigationGuardDialog } = useNavigationGuard(!!activeHandle?.dirty && !activeHandle?.isSubmitting);

  const value: SaveHeaderContextValue = {
    activeTab,
    setActiveTab,
    requestTabChange,
    registerActive,
    activeHandle,
    showSuccess,
    showError,
  };

  return (
    <SaveHeaderContext.Provider value={value}>
      {children}
      <Snackbar
        open={!!snackbar}
        onClose={() => setSnackbar(null)}
        autoHideDuration={snackbar?.severity === 'error' ? 6000 : 3000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        {snackbar ? (
          <Alert sx={{ width: '100%' }} onClose={() => setSnackbar(null)} severity={snackbar.severity} variant="filled">
            {snackbar.message}
          </Alert>
        ) : undefined}
      </Snackbar>
      <ConfirmationDialog
        isOpen={pendingTabChange !== null}
        onClose={(confirmed) => closePendingTabChange(confirmed)}
        title={t('saveHeader.discardTitle')}
        ctaText={t('saveHeader.discardCta')}
        confirmButtonColor="warning"
        loadingButton={false}
        content={<Typography>{t('saveHeader.discardBody')}</Typography>}
      />
      <NavigationGuardDialog />
    </SaveHeaderContext.Provider>
  );
};
